import { useEffect, useMemo, useRef, useState } from 'react';
import { API_URL, TOKEN_KEY, storage } from '../api/client';

/**
 * Several places on one map, each a clickable pin.
 *
 * Google's static tiles as an image, with the pins drawn by us at their
 * projected pixel positions so a click on a pin selects that place. The view
 * starts fitted to every pin plus the customer's own spot; drag to pan.
 */
const TILE = 256;
const worldSize = (zoom) => TILE * (2 ** zoom);
const project = ([lng, lat], zoom) => {
  const size = worldSize(zoom);
  const sin = Math.sin((lat * Math.PI) / 180);
  return { x: ((lng + 180) / 360) * size, y: (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * size };
};
const unproject = ({ x, y }, zoom) => {
  const size = worldSize(zoom);
  const n = Math.PI * (1 - (2 * y) / size);
  return [(x / size) * 360 - 180, (180 / Math.PI) * Math.atan(Math.sinh(n))];
};
const shiftCenter = (center, zoom, dx, dy) => {
  const p = project(center, zoom);
  return unproject({ x: p.x + dx, y: p.y + dy }, zoom);
};
const fit = (coords, width, height) => {
  const lngs = coords.map((c) => c[0]); const lats = coords.map((c) => c[1]);
  const center = [(Math.min(...lngs) + Math.max(...lngs)) / 2, (Math.min(...lats) + Math.max(...lats)) / 2];
  for (let zoom = 16; zoom > 3; zoom -= 1) {
    const ps = coords.map((c) => project(c, zoom));
    const w = Math.max(...ps.map((p) => p.x)) - Math.min(...ps.map((p) => p.x));
    const h = Math.max(...ps.map((p) => p.y)) - Math.min(...ps.map((p) => p.y));
    if (w < width - 70 && h < height - 90) return { center, zoom };
  }
  return { center, zoom: 4 };
};

const PinsMap = ({ points = [], me, selectedId, onSelect, height = 320 }) => {
  const boxRef = useRef(null);
  const [size, setSize] = useState({ width: 0, height });
  const [view, setView] = useState(null);
  const [drag, setDrag] = useState(null);
  const token = storage.get(TOKEN_KEY);

  const valid = useMemo(() => points.filter((p) => p.coordinates && p.coordinates[0] && p.coordinates[1]), [points]);
  const key = valid.map((p) => p.id).join(',');

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return undefined;
    const measure = () => setSize({ width: el.clientWidth, height });
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [height]);

  useEffect(() => {
    if (!size.width) return;
    const coords = [...valid.map((p) => p.coordinates), ...(me && me[0] ? [me] : [])];
    if (coords.length) setView(fit(coords, size.width, size.height));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, size.width, size.height]);

  const pt = (e) => (e.touches ? { x: e.touches[0].clientX, y: e.touches[0].clientY } : { x: e.clientX, y: e.clientY });
  const onDown = (e) => setDrag({ start: pt(e), dx: 0, dy: 0 });
  const onMove = (e) => { if (drag) { const p = pt(e); setDrag({ ...drag, dx: p.x - drag.start.x, dy: p.y - drag.start.y }); } };
  const onUp = () => {
    if (!drag || !view) { setDrag(null); return; }
    if (Math.abs(drag.dx) > 4 || Math.abs(drag.dy) > 4) setView({ ...view, center: shiftCenter(view.center, view.zoom, -drag.dx, -drag.dy) });
    setDrag(null);
  };

  const mapUrl = token && view && size.width
    ? `${API_URL}/maps/static?center=${view.center[0]},${view.center[1]}&zoom=${view.zoom}`
      + `&w=${Math.min(Math.round(size.width), 640)}&h=${Math.min(Math.round(size.height), 640)}&token=${token}`
    : null;
  const place = (coords) => {
    if (!view) return null;
    const c = project(view.center, view.zoom); const p = project(coords, view.zoom);
    return { left: size.width / 2 + (p.x - c.x), top: size.height / 2 + (p.y - c.y) };
  };
  const changeZoom = (d) => setView((v) => (v ? { ...v, zoom: Math.min(Math.max(v.zoom + d, 3), 20) } : v));
  const shift = drag ? `translate(${drag.dx}px, ${drag.dy}px)` : 'none';

  return (
    <div
      ref={boxRef}
      className="mapPicker"
      style={{ height, userSelect: 'none' }}
      onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={() => setDrag(null)}
      onTouchStart={onDown} onTouchMove={onMove} onTouchEnd={onUp}
    >
      <div style={{ position: 'absolute', inset: 0, transform: shift }}>
        {mapUrl ? <img className="mapPickerImg" src={mapUrl} alt="" draggable={false} /> : null}
        {me && me[0] && view ? (() => { const at = place(me); return at ? (
          <div style={{ position: 'absolute', left: at.left - 8, top: at.top - 8, width: 16, height: 16, borderRadius: 8, background: '#1B4DE4', border: '3px solid #fff' }} />
        ) : null; })() : null}
        {view ? valid.map((p) => {
          const at = place(p.coordinates);
          if (!at) return null;
          const on = p.id === selectedId;
          return (
            <button
              type="button"
              key={p.id}
              onClick={(e) => { e.stopPropagation(); onSelect && onSelect(p.id); }}
              onMouseDown={(e) => e.stopPropagation()}
              title={p.label}
              style={{
                position: 'absolute', left: at.left - 18, top: at.top - 44, width: 36, background: 'none', border: 'none', padding: 0,
                cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: on ? 5 : 1,
              }}
            >
              {on ? (
                <span style={{ background: 'var(--ink)', color: '#fff', fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 8, marginBottom: 4, whiteSpace: 'nowrap' }}>{p.label}</span>
              ) : null}
              <span style={{
                width: 32, height: 32, borderRadius: 16, background: on ? 'var(--primary-light)' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: `2px solid ${on ? 'var(--primary)' : 'var(--ink-2)'}`, fontSize: 14, transform: on ? 'scale(1.15)' : 'none',
              }}
              >
                {p.icon || '🔧'}
              </span>
              <span style={{ width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: `8px solid ${on ? 'var(--primary)' : 'var(--ink-2)'}` }} />
            </button>
          );
        }) : null}
      </div>
      <div style={{ position: 'absolute', right: 10, top: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <button type="button" className="btn light sm" onClick={() => changeZoom(1)} onMouseDown={(e) => e.stopPropagation()}>+</button>
        <button type="button" className="btn light sm" onClick={() => changeZoom(-1)} onMouseDown={(e) => e.stopPropagation()}>−</button>
      </div>
    </div>
  );
};

export default PinsMap;
